import { getTenantId } from './tenantService';

import { 
  getFirestore, doc, setDoc, getDoc, updateDoc, deleteDoc, 
  collection, onSnapshot, query, where, orderBy, limit, addDoc, getDocs, writeBatch, increment, deleteField, serverTimestamp,
  DocumentReference, CollectionReference, DocumentData
} from "firebase/firestore";
import { db } from "../firebase";
import { 
  GlobalSettings, DeliverySettings, MenuGraphicsSettings, Product, Ingredient, 
  MenuConfig, CategoryConfig, LiveOrder, ParsedMenuData, Tenant, VisitedTenant, 
  Customer, CustomerMessage, IntercomSession, RiderLocation, PublicOrderStat,
  RiderProfile, RiderStatus, DeliveryRequest, ChatMessage, CallSession
} from "../types";

// --- TENANT HELPERS ---


export const getDocRef = (path: string, id?: string): any => {
    const tenantId = getTenantId() || 'root';
    const basePath = tenantId === 'root' ? path : `tenants/${tenantId}/${path}`;
    return id ? doc(db, basePath, id) : collection(db, basePath);
};

export * from './productService';
export * from './orderService';
export * from './riderService';
export * from './tenantService';

export const cleanPayload = (obj: any): any => {
  if (obj === undefined || obj === null) return {};
  // JSON.stringify natively drops keys with undefined values.
  // We parse it back to get a clean object.
  return JSON.parse(JSON.stringify(obj));
};

// ... (existing updateGlobalSettings, saveSettings, subscribeToSettings ...) 

export const saveGeneratedCoupon = async (code: string, expiresAt: number, payload: any = {}): Promise<void> => {
    let tenantId = getTenantId();
    if (!tenantId) tenantId = 'root';
    try {
        const ref = doc(db, tenantId === 'root' ? 'generated_coupons' : `tenants/${tenantId}/generated_coupons`, code);
        await setDoc(ref, {
            ...payload,
            code,
            expiresAt,
            used: false,
            createdAt: serverTimestamp()
        }, { merge: true });
    } catch (e) {
        console.error("Error saving generated coupon to db:", e);
    }
};


export const getGeneratedCoupon = async (code: string): Promise<any | null> => {
    let tenantId = getTenantId();
    if (!tenantId) tenantId = 'root';
    try {
        const ref = doc(db, tenantId === 'root' ? 'generated_coupons' : `tenants/${tenantId}/generated_coupons`, code);
        const snap = await getDoc(ref);
        if (snap.exists()) {
            return snap.data();
        }
    } catch (e) {
        console.error("Error getting generated coupon from db:", e);
    }
    return null;
};


export const consumeGeneratedCoupon = async (code: string): Promise<void> => {
    let tenantId = getTenantId();
    if (!tenantId) tenantId = 'root';
    try {
        // Option 1: mark as used
        const ref = doc(db, tenantId === 'root' ? 'generated_coupons' : `tenants/${tenantId}/generated_coupons`, code);
        await updateDoc(ref, { used: true, usedAt: serverTimestamp() });
        // Option 2: could just delete it too, but better keep it and mark as used.
    } catch (e) {
        console.error("Error consuming generated coupon in db:", e);
    }
};


export const updateGlobalSettings = async (updates: Partial<GlobalSettings>): Promise<void> => {
    const ref = getDocRef("settings", "global");
    const payload = cleanPayload(updates);
    await setDoc(ref, payload, { merge: true });

    // --- SYNC TO REGISTRY (FIX FOR LANDING PAGE & RIDER DASHBOARD) ---
    // If the App Name, Opening Hours or Special Closures change, we must update the public registry.
    const tenantId = getTenantId();
    if (tenantId && tenantId !== 'root') {
        try {
            const registryUpdates: any = {};
            if (updates.appName) registryUpdates.name = updates.appName;
            if (updates.openingHours) registryUpdates.openingHours = updates.openingHours;
            if (updates.specialClosures) registryUpdates.specialClosures = updates.specialClosures;
            if (updates.isDeliveryEnabled !== undefined) {
                registryUpdates.isDeliveryEnabled = updates.isDeliveryEnabled;
            } else if (updates.orderModes?.delivery !== undefined) {
                registryUpdates.isDeliveryEnabled = updates.orderModes.delivery.isActive;
            }
            if (updates.isDeliveryPaused !== undefined) registryUpdates.isDeliveryPaused = updates.isDeliveryPaused;
            if (updates.orderModes !== undefined) registryUpdates.orderModes = updates.orderModes;
            
            if (Object.keys(registryUpdates).length > 0) {
                // Update New Registry
                await setDoc(doc(db, "tenantsRegistry", tenantId), registryUpdates, { merge: true });
                // Update Legacy Registry (fallback)
                await updateDoc(doc(db, "saas_tenants", tenantId), registryUpdates).catch(() => {});
            }
        } catch (e) {
            console.warn("Failed to sync settings to registry:", e);
        }
    }
};


export const saveSettings = async (
    appName: string, 
    whatsappNumber: string, 
    shopAddress: string, 
    takeAwayFee: number, 
    disposableFee: number | undefined, 
    categoryOrder: string[] | undefined, 
    socialLinks: any, 
    openingHours: any, 
    isDeliveryEnabled: boolean | undefined, 
    printerSettings: any, 
    soundSettings: any, 
    nexiSettings: any, 
    satispaySettings: any, 
    paypalSettings: any, 
    deliveryDiscount: number | undefined, 
    orderModes: any, 
    deliveryScheduleMode: any, 
    deliveryStartTime: any, 
    deliveryEndTime: any, 
    isRiderTrackingEnabled: boolean | undefined, 
    ipCameras: any, 
    loyaltyProgram: any, 
    isProRetailMode: boolean | undefined, 
    coupons: any, 
    deliveryWaitTime: any, 
    servingOptions: any, 
    fiscalPrinterSettings: any, 
    riderUnlockPin?: string, 
    isDeliveryPaused?: boolean, 
    deliveryPauseMessage?: string, 
    requireTableNumber?: boolean,
    activeShopMessage?: any,
    activeGlobalPromo?: any,
    brandPresentation?: string, 
    events?: any,
    brandTitle?: string,
    customerTagPresets?: string[],
    upsellSettings?: any,
    specialClosures?: any, 
    languageData?: any, 
    appSubtitle?: string, 
    discountApplicability?: any,
    deliveryCutoffTime?: string,
    isDeliveryCutoffEnabled?: boolean 
): Promise<void> => {
  const updates: Partial<GlobalSettings> = { 
      appName, appSubtitle, brandPresentation, brandTitle, whatsappNumber, takeAwayFee, disposableFee, deliveryDiscount, deliveryWaitTime, 
      categoryOrder, socialLinks, openingHours, isDeliveryEnabled, printerSettings, soundSettings, 
      nexiSettings, satispaySettings, paypalSettings, orderModes, deliveryScheduleMode, 
      deliveryStartTime, deliveryEndTime, isRiderTrackingEnabled, riderUnlockPin, ipCameras, 
      loyaltyProgram, isProRetailMode, coupons, events, servingOptions, fiscalPrinterSettings, 
      isDeliveryPaused, deliveryPauseMessage, requireTableNumber, 
      activeShopMessage, activeGlobalPromo, customerTagPresets, upsellSettings, specialClosures,
      languageData, discountApplicability, deliveryCutoffTime, isDeliveryCutoffEnabled
  };
  await updateGlobalSettings(updates);
};


export const subscribeToSettings = (callback: (settings: GlobalSettings) => void) => {
  const ref = getDocRef("settings", "global");
  return onSnapshot(ref, (doc: any) => {
    if (doc.exists()) callback(doc.data() as GlobalSettings);
    else callback({} as GlobalSettings);
  }, (err: any) => console.error("FirebaseError in settings listener:", err));
};


export const saveDeliverySettings = async (settings: DeliverySettings) => {
  const ref = getDocRef("settings", "delivery");
  await setDoc(ref, cleanPayload(settings), { merge: true });
  
  // Sync shopLatLng to registry for Rider distance filtering
  const tenantId = getTenantId();
  if (tenantId && tenantId !== 'root' && settings.shopLatLng) {
      try {
          await setDoc(doc(db, "tenantsRegistry", tenantId), { 
              shopLatLng: settings.shopLatLng 
          }, { merge: true });
      } catch (e) {
          console.warn("Failed to sync shopLatLng to registry:", e);
      }
  }
};


export const subscribeToDeliverySettings = (callback: (settings: DeliverySettings) => void) => {
  const ref = getDocRef("settings", "delivery");
  return onSnapshot(ref, (doc: any) => {
    if (doc.exists()) callback(doc.data() as DeliverySettings);
    else callback({ shopAddress: "", zones: [] });
  }, (err: any) => console.error("FirebaseError in delivery listener:", err));
};


export const saveGraphicsSettings = async (settings: MenuGraphicsSettings) => {
  const ref = getDocRef("settings", "graphics");
  await setDoc(ref, cleanPayload(settings), { merge: true });

  // --- SYNC LOGO TO REGISTRY (FIX FOR LANDING PAGE) ---
  const tenantId = getTenantId();
  if (tenantId && tenantId !== 'root' && settings.appLogoType === 'IMAGE' && settings.customLogoUrl) {
      try {
          // Update New Registry
          await setDoc(doc(db, "tenantsRegistry", tenantId), { logoUrl: settings.customLogoUrl }, { merge: true });
          // Update Legacy Registry
          await updateDoc(doc(db, "saas_tenants", tenantId), { logoUrl: settings.customLogoUrl }).catch(() => {});
      } catch (e) {
          console.warn("Failed to sync Logo to registry:", e);
      }
  }
};


export const subscribeToGraphicsSettings = (callback: (settings: MenuGraphicsSettings) => void) => {
  const ref = getDocRef("settings", "graphics");
  return onSnapshot(ref, (doc: any) => {
    if (doc.exists()) callback(doc.data() as MenuGraphicsSettings);
    else callback({});
  }, (err: any) => console.error("FirebaseError in graphics listener:", err));
};


export const setGlobalProRetailMode = async (enabled: boolean) => {
    const ref = getDocRef("settings", "global");
    await updateDoc(ref, { isProRetailMode: enabled });
};

// ... (Products, Ingredients, Menus, Categories same as before) ...

export const subscribeToCustomers = (callback: (customers: Customer[]) => void) => {
  const ref = getDocRef("customers");
  return onSnapshot(ref, (snapshot: any) => {
    callback(snapshot.docs.map((d: any) => ({ id: d.id, ...(d.data() as any) } as Customer)));
  });
};


export const getCustomers = async (): Promise<Customer[]> => {
    const { getDocs } = await import('firebase/firestore');
    const ref = getDocRef("customers");
    const snapshot = await getDocs(ref);
    return snapshot.docs.map((d: any) => ({ id: d.id, ...(d.data() as any) } as Customer));
};


export const createCustomer = async (data: Partial<Customer>) => {
  const ref = getDocRef("customers");
  await addDoc(ref, cleanPayload(data));
};


export const updateCustomer = async (id: string, data: Partial<Customer>) => {
  const ref = getDocRef("customers", id);
  await updateDoc(ref, cleanPayload(data));
};


export const deleteCustomer = async (id: string) => {
  const ref = getDocRef("customers", id);
  await deleteDoc(ref);
};


export const getCustomerByPhone = async (phone: string): Promise<Customer | null> => {
    const ref = getDocRef("customers");
    const q = query(ref, where("phone", "==", phone), limit(1));
    const snap = await getDocs(q);
    if (!snap.empty) return { id: snap.docs[0].id, ...(snap.docs[0].data() as any) } as Customer;
    return null;
};


export const sendCustomerMessage = async (msg: CustomerMessage) => {
    const ref = getDocRef("messages");
    await addDoc(ref, cleanPayload(msg));
};


export const subscribeToMessages = (callback: (msgs: CustomerMessage[]) => void) => {
    const ref = getDocRef("messages");
    const q = query(ref, orderBy("timestamp", "desc"), limit(50));
    return onSnapshot(q, (snap) => callback(snap.docs.map((d: any) => ({ ...(d.data() as any), id: d.id } as CustomerMessage))));
};


export const markMessageAsRead = async (id: string, explicitTenantId?: string) => {
    const tenantId = explicitTenantId || getTenantId() || 'root';
    const basePath = tenantId === 'root' ? 'messages' : `tenants/${tenantId}/messages`;
    const ref = doc(db, basePath, id);
    await updateDoc(ref, { isRead: true });
};


export const deleteMessage = async (id: string, explicitTenantId?: string) => {
    const tenantId = explicitTenantId || getTenantId() || 'root';
    const basePath = tenantId === 'root' ? 'messages' : `tenants/${tenantId}/messages`;
    const ref = doc(db, basePath, id);
    await deleteDoc(ref);
};


export const subscribeToPublicStats = (limitCount: number, callback: (stats: PublicOrderStat[]) => void) => {
    const ref = getDocRef("public_order_stats");
    const q = query(ref, limit(limitCount));
    return onSnapshot(q, (snapshot: any) => {
        callback(snapshot.docs.map((d: any) => ({ id: d.id, ...(d.data() as any) } as PublicOrderStat)));
    }, (err: any) => console.error("FirebaseError in public stats listener:", err));
};


export const sendIntercomOffer = async (sdp: string, sessionId: string, targetKioskId?: string | null, targetKioskName?: string | null) => {
    const ref = getDocRef("intercom", "session");
    await setDoc(ref, { 
        type: 'OFFER', 
        sdp, 
        sessionId, 
        timestamp: Date.now(), 
        sender: 'ADMIN',
        targetKioskId: targetKioskId || null,
        targetKioskName: targetKioskName || null
    });
};


export const sendIntercomRingAdmins = async (kioskId?: string | null, kioskName?: string | null) => {
    const ref = getDocRef("intercom", "session");
    await setDoc(ref, { 
        type: 'RING_ADMINS', 
        timestamp: Date.now(), 
        sender: 'LOCAL',
        kioskId: kioskId || null,
        kioskName: kioskName || null
    });
};


export const sendIntercomAnswer = async (sdp: string, sessionId: string) => {
    const ref = getDocRef("intercom", "session");
    await setDoc(ref, { type: 'ANSWER', sdp, sessionId, timestamp: Date.now(), sender: 'LOCAL' });
};


export const sendIntercomCandidate = async (candidate: RTCIceCandidate, sender: 'ADMIN' | 'LOCAL', sessionId: string) => {
    const ref = collection(getDocRef("intercom", "session"), "candidates");
    await addDoc(ref, { candidate: JSON.stringify(candidate), sender, sessionId, timestamp: Date.now() });
};


export const sendIntercomHangup = async () => {
    const ref = getDocRef("intercom", "session");
    await setDoc(ref, { type: 'HANGUP', timestamp: Date.now() });
};


export const sendIntercomCommand = async (cmd: string) => {
    const ref = getDocRef("intercom", "session");
    await updateDoc(ref, { type: 'CMD', command: cmd, timestamp: Date.now() });
};


export const updateIntercomAdminState = async (videoEnabled: boolean) => {
    const ref = getDocRef("intercom", "session");
    await updateDoc(ref, { adminVideoEnabled: videoEnabled });
};


export const subscribeToIntercomSession = (callback: (session: IntercomSession | null) => void) => {
    const ref = getDocRef("intercom", "session");
    return onSnapshot(ref, (doc: any) => {
        if (doc.exists()) callback(doc.data() as IntercomSession);
        else callback(null);
    });
};


export const subscribeToIntercomCandidates = (callback: (data: any) => void) => {
    const ref = collection(getDocRef("intercom", "session"), "candidates");
    // Only listen to candidates from the last 2 minutes to avoid downloading thousands of old candidates
    const q = query(ref, where("timestamp", ">=", Date.now() - 120000), orderBy("timestamp", "asc"));
    return onSnapshot(q, (snapshot: any) => {
        snapshot.docChanges().forEach((change: any) => {
            if (change.type === "added") callback(change.doc.data());
        });
    });
};


export const performBackup = async (): Promise<any> => {
    const backupData: any = {
        version: "2.1",
        timestamp: new Date().toISOString(),
        tenantId: getTenantId(),
        settings: {},
        collections: {},
        globalCollections: {}
    };
    
    // 1. Settings (Tenant Scoped)
    const settingsTypes = ['global', 'delivery', 'graphics', 'riderLocation'];
    for (const type of settingsTypes) {
        const ref = getDocRef("settings", type);
        const snap = await getDoc(ref);
        if (snap.exists()) {
            backupData.settings[type] = snap.data();
        }
    }
    
    // 2. Tenant Collections
    const collectionsToBackup = ['products', 'ingredients', 'menus', 'categories', 'customers', 'orders', 'messages', 'site_visits', 'public_order_stats'];
    for (const colName of collectionsToBackup) {
        try {
            const ref = getDocRef(colName);
            const snap = await getDocs(ref);
            backupData.collections[colName] = snap.docs.map(doc => ({_id: doc.id, ...(doc.data() as any)}));
        } catch (e) {
            console.warn(`Skipping tenant collection ${colName} due to error:`, e);
        }
    }
    
    // 3. Global Collections
    const globalCollectionsToBackup = ['riders', 'rider_shifts', 'rider_workdays', 'rider_feedbacks', 'delivery_requests'];
    for (const colName of globalCollectionsToBackup) {
        try {
            const ref = collection(db, colName);
            const snap = await getDocs(ref);
            backupData.globalCollections[colName] = snap.docs.map(doc => ({_id: doc.id, ...(doc.data() as any)}));
        } catch (e) {
            console.warn(`Skipping global collection ${colName} due to error:`, e);
        }
    }
    
    return backupData;
};


export const performRestore = async (data: any) => {
    // 1. Restore Settings
    if (data.settings) {
        for (const [key, value] of Object.entries(data.settings)) {
            if (value) await setDoc(getDocRef("settings", key), value as any, { merge: true });
        }
    }

    // 2. Restore Tenant Collections (Structure >= v2.0)
    if (data.collections) {
        for (const [colName, items] of Object.entries(data.collections)) {
            const list = items as any[];
            for (const item of list) {
                const { _id, ...docData } = item;
                if (_id) await setDoc(getDocRef(colName, _id), docData, { merge: true });
            }
        }
    }
    
    // 2b. Restore Global Collections (Structure >= v2.1)
    if (data.globalCollections) {
        for (const [colName, items] of Object.entries(data.globalCollections)) {
            const list = items as any[];
            for (const item of list) {
                const { _id, ...docData } = item;
                if (_id) await setDoc(doc(db, colName, _id), docData, { merge: true });
            }
        }
    }

    // 3. Restore Root Level Arrays (Old Structure / Backward Compatibility)
    const legacyCollections = ['products', 'menus', 'categories', 'ingredients', 'customers', 'orders', 'messages'];
    
    for (const colName of legacyCollections) {
        if (data[colName] && Array.isArray(data[colName])) {
            for (const item of data[colName]) {
                const { id, _id, ...rest } = item;
                const docId = id || _id;
                if (docId) {
                    await setDoc(getDocRef(colName, docId), rest, { merge: true });
                }
            }
        }
    }
};


export const saveParsedData = async (data: ParsedMenuData): Promise<{ added: number }> => {
    let added = 0;
    const batch = writeBatch(db);
    
    // 1. SAVE MENUS (Merge if exists)
    if (data.menus) {
        for (const menu of data.menus) {
            const ref = getDocRef("menus", menu.id);
            // Use merge: true to update existing or create new
            batch.set(ref, { ...menu, isActive: true }, { merge: true });
        }
    }

    // 2. SAVE CATEGORIES (Merge if exists)
    if (data.categories) {
        for (const cat of data.categories) {
            const ref = getDocRef("categories", cat.id);
            batch.set(ref, cleanPayload(cat), { merge: true });
        }
    }

    // 3. SAVE PRODUCTS (Always add new)
    if (data.products) {
        for (const prod of data.products) {
            // Generate unique ID for new products
            const id = `PROD_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
            const ref = getDocRef("products", id);
            batch.set(ref, { ...prod, id, isActive: true });
            added++;
        }
    }

    // 4. SAVE INGREDIENTS (Always add new)
    if (data.ingredients) {
        for (const ing of data.ingredients) {
             const id = `ING_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
             const ref = getDocRef("ingredients", id);
             batch.set(ref, { ...ing, id, isActive: true });
        }
    }
    
    await batch.commit();
    return { added };
};


export const savePlatformBranding = async (data: any) => {
    const ref = doc(db, "platform", "graphics");
    await setDoc(ref, cleanPayload(data), { merge: true });
};


export const getPlatformGraphics = async (): Promise<any> => {
    let data = null;
    try {
        const snap = await getDoc(doc(db, "platform", "graphics"));
        if (snap.exists()) {
            data = snap.data();
        } else {
            const rootSnap = await getDoc(getDocRef("settings", "global"));
            if (rootSnap.exists()) {
                data = rootSnap.data();
            }
        }
    } catch(e) { console.error("Error reading platform graphics: ", e); }
    return data;
};


export const logSiteVisit = async (source?: string) => {
    const tenantId = getTenantId() || 'root';
    const dateStr = new Date().toLocaleDateString('en-CA');
    const visitRef = doc(db, `tenants/${tenantId}/site_visits`, dateStr);
    try {
        let actualSource = source;
        if (!actualSource && typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            actualSource = urlParams.get('ref') || undefined;
        }

        const isPWA = typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone);

        const updateData: any = {
            count: increment(1),
            timestamp: serverTimestamp()
        };
        if (actualSource === 'vetrina') {
            updateData.vetrinaCount = increment(1);
            if (typeof window !== 'undefined') {
                localStorage.setItem(`vetrina_logged_${tenantId}_${dateStr}`, 'true');
            }
        }
        if (isPWA) {
            updateData.pwaCount = increment(1);
        }
        await setDoc(visitRef, updateData, { merge: true });
    } catch(e) {
        console.error("Error logging visit", e);
    }
};


export const unlogSiteVisit = async () => {
    const tenantId = getTenantId() || 'root';
    const dateStr = new Date().toLocaleDateString('en-CA');
    const visitRef = doc(db, `tenants/${tenantId}/site_visits`, dateStr);
    try {
        const wasVetrina = typeof window !== 'undefined' && localStorage.getItem(`vetrina_logged_${tenantId}_${dateStr}`) === 'true';
        
        const updateData: any = {
            count: increment(-1),
            timestamp: serverTimestamp()
        };
        if (wasVetrina) {
            updateData.vetrinaCount = increment(-1);
        }
        await setDoc(visitRef, updateData, { merge: true });
    } catch(e) {
        console.error("Error unlogging visit", e);
    }
};


export const getSiteVisits = async (specificTenantId?: string): Promise<{date: string, count: number, vetrinaCount?: number, pwaCount?: number}[]> => {
    const tenantId = specificTenantId || getTenantId() || 'root';
    const ref = collection(db, `tenants/${tenantId}/site_visits`);
    try {
        const snap = await getDocs(ref);
        return snap.docs.map((d: any) => ({ 
            date: d.id, 
            count: d.data().count || 0,
            vetrinaCount: d.data().vetrinaCount || 0,
            pwaCount: d.data().pwaCount || 0
        })).sort((a,b) => a.date.localeCompare(b.date));
    } catch(e) {
        console.error("Error fetching site visits", e);
        return [];
    }
};

export const clearTenantOrders = async (): Promise<void> => {
    const batch = writeBatch(db);
    
    // Clear live_orders (actual)
    const ref1 = getDocRef('live_orders');
    const snap1 = await getDocs(ref1);
    snap1.docs.forEach((d) => {
        batch.delete(d.ref);
    });
    
    // Clear orders (legacy if any)
    try {
        const ref2 = getDocRef('orders');
        const snap2 = await getDocs(ref2);
        snap2.docs.forEach((d) => {
            batch.delete(d.ref);
        });
    } catch (e) {
        console.warn("Could not clear legacy orders", e);
    }
    
    await batch.commit();
};

// --- ACTIVE KIOSKS MANAGEMENT ---

export const registerKioskDevice = async (deviceId: string): Promise<{ name: string }> => {
    const allRef = getDocRef("active_kiosks");
    const snap = await getDocs(allRef);
    const now = Date.now();
    
    // Clean up kiosks not seen in the last 5 minutes to free names
    const fiveMinutesAgo = now - 300000;
    const activeKiosksList: any[] = [];
    
    const batch = writeBatch(db);
    snap.docs.forEach((doc: any) => {
        const data = doc.data() as { lastSeen: number; name: string };
        if (data.lastSeen < fiveMinutesAgo && doc.id !== deviceId) {
            batch.delete(doc.ref);
        } else {
            activeKiosksList.push({ id: doc.id, lastSeen: data.lastSeen, name: data.name });
        }
    });
    
    // Check if we already have this device in the list of active/unexpired kiosks
    const existing = activeKiosksList.find(k => k.id === deviceId);
    if (existing) {
        const ref = getDocRef("active_kiosks", deviceId);
        batch.set(ref, { lastSeen: now, name: existing.name }, { merge: true });
        await batch.commit();
        return { name: existing.name };
    }
    
    // Find the lowest free index for TOTEM X (TOTEM 1, TOTEM 2...)
    let targetIndex = 1;
    while (true) {
        const testName = `TOTEM 0${targetIndex}`;
        const nameTaken = activeKiosksList.some(k => k.name === testName);
        if (!nameTaken) {
            break;
        }
        targetIndex++;
    }
    
    const newName = `TOTEM 0${targetIndex}`;
    const ref = getDocRef("active_kiosks", deviceId);
    batch.set(ref, { id: deviceId, name: newName, lastSeen: now }, { merge: true });
    await batch.commit();
    return { name: newName };
};

export const heartbeatKioskDevice = async (deviceId: string, name: string) => {
    const ref = getDocRef("active_kiosks", deviceId);
    await setDoc(ref, { lastSeen: Date.now(), name }, { merge: true });
};

export const subscribeToActiveKiosks = (callback: (kiosks: any[]) => void) => {
    const ref = getDocRef("active_kiosks");
    return onSnapshot(ref, (snap: any) => {
        const fiveMinutesAgo = Date.now() - 300000;
        const list = snap.docs
            .map((d: any) => ({ id: d.id, ...(d.data() as any) }))
            .filter((k: any) => k.lastSeen >= fiveMinutesAgo)
            .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
        callback(list);
    });
};

// --- RIDER NETWORK SERVICES ---

